import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
  Checkbox,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import paymentIcon from "../../../static/media/icon/payment.svg";
import xmlIcon from "../../../static/media/icon/xml.svg";

const tdLabelSx = {
  width: 180,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };
const inputSx = { width: "50%" };

const norm = (v) => String(v || "").trim().toUpperCase();

const InfoInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloadingXml, setDownloadingXml] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    sev: "error",
  });

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true);
      setError(false);
      try {
        const r = await fetch(`/facturas/documentos/${id}/`, { credentials: "include" });
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("application/json")) throw new Error();
        if (!r.ok) throw new Error();
        const j = await r.json();
        setDoc(j);
      } catch {
        setError(true);
        setDoc(null);
        setSnack({
          open: true,
          msg: "Documento no encontrado",
          sev: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchDoc();
  }, [id]);

  const esCompra = norm(doc?.origen) === "COMPRA";
  const mostrarVendedor = doc?.vendedor && doc?.vendedor?.nombre;
  const identificacion = doc?.persona_detalle?.identificacion || "";

  const tipoDocumentoLabel = useMemo(() => {
    if (!doc) return "";
    if (esCompra) return "Compra";

    const tipo = norm(doc?.tipo_documento);

    if (tipo === "04") return "Nota de crédito";
    if (tipo === "05") return "Nota de débito";
    if (tipo === "06") return "Guía de remisión";
    if (tipo === "07") return "Retención";
    if (tipo === "01") return "Factura";

    const numero = norm(doc?.numero_documento);
    const descripcion = norm(doc?.descripcion);
    const referencia = norm(doc?.referencia);

    if (descripcion.includes("NOTA DE CRÉDITO") || descripcion.includes("NOTA DE CREDITO")) {
      return "Nota de crédito";
    }

    if (referencia.includes("NOTA DE CRÉDITO") || referencia.includes("NOTA DE CREDITO")) {
      return "Nota de crédito";
    }

    if (numero.startsWith("NC ")) return "Nota de crédito";
    if (numero.startsWith("ND ")) return "Nota de débito";
    if (numero.startsWith("GR ")) return "Guía de remisión";
    if (numero.startsWith("RET ")) return "Retención";

    return "Factura";
  }, [doc, esCompra]);

  const tituloDocumento = useMemo(() => {
    if (!doc) return "Documento";
    if (esCompra) return "Compra";
    return tipoDocumentoLabel;
  }, [doc, esCompra, tipoDocumentoLabel]);

  const handleDownloadXml = async () => {
    setDownloadingXml(true);
    try {
      const r = await fetch(`/facturas/documentos/${id}/xml/`, {
        credentials: "include",
      });

      if (!r.ok) {
        let msg = "Documento no encontrado";
        try {
          const text = await r.text();
          if (text) msg = "Documento no encontrado";
        } catch {}
        throw new Error(msg);
      }

      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const disposition = r.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const fileName = match?.[1] || `${doc?.numero_documento || "documento"}.xml`;

      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSnack({
        open: true,
        msg: "XML descargado correctamente",
        sev: "success",
      });
    } catch {
      setSnack({
        open: true,
        msg: "Documento no encontrado",
        sev: "error",
      });
    } finally {
      setTimeout(() => {
        setDownloadingXml(false);
      }, 700);
    }
  };

  if (loading) {
    return (
      <>
        <Box sx={{ mt: 5, textAlign: "center" }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Cargando documento…</Typography>
        </Box>

        <Snackbar
          open={snack.open}
          autoHideDuration={3200}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={snack.sev}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </>
    );
  }

  if (error || !doc) {
    return (
      <>
        <Box sx={{ mt: 5, textAlign: "center" }}>
          <Typography color="error">Documento no encontrado</Typography>
        </Box>

        <Snackbar
          open={snack.open}
          autoHideDuration={3200}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={snack.sev}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Información del Documento – {tituloDocumento}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            {!esCompra && (
              <Tooltip title="Descargar XML" arrow>
                <IconButton onClick={handleDownloadXml}>
                  <img src={xmlIcon} alt="Descargar XML" style={{ width: 26, height: 26 }} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Registrar Pago" arrow>
              <IconButton
                onClick={() =>
                  navigate(`/admin-panel/pagos/${id}?origen=${esCompra ? "COMPRA" : "VENTA"}`, {
                    state: {
                      origen: esCompra ? "COMPRA" : "VENTA",
                      persona: {
                        identificacion,
                        tipo_persona:
                          doc?.persona_detalle?.tipo_persona || (esCompra ? "PROVEEDOR" : "CLIENTE"),
                        razon_social: doc?.persona_detalle?.razon_social || doc?.persona || "",
                      },
                    },
                  })
                }
              >
                <img src={paymentIcon} alt="Registrar Pago" style={{ width: 28, height: 28 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={tdLabelSx}>Fecha de Emisión:</TableCell>
              <TableCell sx={tdFieldSx}>
                <TextField value={doc.fecha_emision} InputProps={{ readOnly: true }} sx={inputSx} />
              </TableCell>

              <TableCell sx={tdLabelSx}>Tipo de Documento:</TableCell>
              <TableCell sx={tdFieldSx}>
                <TextField value={tipoDocumentoLabel} InputProps={{ readOnly: true }} sx={inputSx} />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={tdLabelSx}># Documento:</TableCell>
              <TableCell sx={tdFieldSx}>
                <TextField value={doc.numero_documento} InputProps={{ readOnly: true }} sx={inputSx} />
              </TableCell>

              <TableCell sx={tdLabelSx}>Autorización:</TableCell>
              <TableCell sx={tdFieldSx}>
                <TextField value={doc.autorizacion || ""} InputProps={{ readOnly: true }} sx={{ width: "100%" }} />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={tdLabelSx}>Persona:</TableCell>
              <TableCell sx={tdFieldSx} colSpan={3}>
                <TextField value={doc.persona} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={tdLabelSx}>Identificación:</TableCell>
              <TableCell sx={tdFieldSx} colSpan={3}>
                <TextField value={identificacion} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={tdLabelSx}>Referencia:</TableCell>
              <TableCell sx={tdFieldSx} colSpan={3}>
                <TextField value={doc.referencia || ""} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
              </TableCell>
            </TableRow>

            {mostrarVendedor && (
              <TableRow>
                <TableCell sx={tdLabelSx}>Vendedor:</TableCell>
                <TableCell sx={tdFieldSx} colSpan={3}>
                  <TextField value={doc.vendedor.nombre} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
                </TableCell>
              </TableRow>
            )}

            {!esCompra && (
              <TableRow>
                <TableCell sx={tdLabelSx}>Vencimiento (días):</TableCell>
                <TableCell sx={tdFieldSx}>
                  <TextField value={doc.vencimiento_dias || 0} InputProps={{ readOnly: true }} sx={inputSx} />
                </TableCell>

                <TableCell sx={tdLabelSx}>Entregado:</TableCell>
                <TableCell sx={tdFieldSx}>
                  <Checkbox checked={Boolean(doc.entregado)} disabled />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={downloadingXml} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Descargando XML</DialogTitle>
        <DialogContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <CircularProgress />
          <Typography sx={{ fontWeight: 600 }}>Preparando descarga…</Typography>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InfoInvoice;